import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { isSessionValid } from "@/utils/auth";
import logger from "@/utils/logger";

/**
 * Role-based protected route component
 * @param {Object} props
 * @param {string[]} props.requiredRoles - Array of required roles (e.g., ['developer', 'admin'])
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} props.fallbackPath - Path to redirect to if unauthorized (default: '/login')
 */
export default function RoleProtectedRoute({ 
  children, 
  requiredRoles = [], 
  fallbackPath = "/login" 
}) {
  const [authState, setAuthState] = useState({
    loading: true,
    authenticated: false,
    authorized: false,
    userRole: null
  });

  const verifyRoleAccess = async () => {
    try {
      // First check basic session validity
      if (!isSessionValid()) {
        logger.log("Session invalid, redirecting to login");
        setAuthState({
          loading: false,
          authenticated: false,
          authorized: false,
          userRole: null
        });
        return;
      }

      // Get the JWT token from storage
      const authToken = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      
      if (!authToken) {
        logger.log("No auth token found, redirecting to login");
        setAuthState({
          loading: false,
          authenticated: false,
          authorized: false,
          userRole: null
        });
        return;
      }

      // Verify the JWT token and get user role via API
      const response = await fetch("/api/verify-jwt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        logger.log("JWT verification failed, redirecting to login");
        setAuthState({
          loading: false,
          authenticated: false,
          authorized: false,
          userRole: null
        });
        return;
      }

      const data = await response.json();
      
      // Extract role from the response
      const userRole = data.customer?.metadata?.role || data.tokenData?.metadata?.role;
      
      if (!userRole) {
        logger.log("No role found in token metadata");
        setAuthState({
          loading: false,
          authenticated: true,
          authorized: false,
          userRole: null
        });
        return;
      }

      // Check if user has required role
      const hasRequiredRole = requiredRoles.length === 0 || requiredRoles.includes(userRole);
      
      logger.log(`User role: ${userRole}, Required roles: ${requiredRoles.join(', ')}, Authorized: ${hasRequiredRole}`);
      
      setAuthState({
        loading: false,
        authenticated: true,
        authorized: hasRequiredRole,
        userRole: userRole
      });

    } catch (error) {
      logger.error("Error verifying role access:", error);
      setAuthState({
        loading: false,
        authenticated: false,
        authorized: false,
        userRole: null
      });
    }
  };

  useEffect(() => {
    verifyRoleAccess();
  }, []);

  // Show loading state
  if (authState.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Verifying access...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!authState.authenticated) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Show unauthorized message if authenticated but not authorized
  if (!authState.authorized) {
    return <Navigate to={"/"} replace />;
  }

  // User is authenticated and authorized
  return children;
}
